import React from 'react';
import { Helmet } from 'react-helmet-async';
import { IS_PROD } from '../../lib/env';
import { LAUNCH_PHASE, APP_STORE_ID } from '../../lib/storeLinks';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  noIndex?: boolean;
  structuredData?: object;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
  };
  /**
   * Emit the iOS Smart App Banner meta at launch.
   * Galleries are web-only by design — the native app excludes /gallery/*
   * from its universal-link allowlist (muse-app AndroidManifest.xml /
   * AASA) — so Gallery passes appBanner={false}.
   */
  appBanner?: boolean;
}

const SITE_NAME = 'Muse';
const DEFAULT_TITLE =
  'Muse — Pose Library, Shoot Planning & Client Galleries';
// One canonical site description — availability-free so it never goes stale
// as the app moves from pre-order to launched (the CTAs carry the
// availability copy, driven by the launch phase). index.html and
// manifest.json mirror this text.
export const SITE_DESCRIPTION =
  'Muse keeps your pose references, bookings, and client galleries in one app. Saved poses open offline on set, shot lists attach to bookings, and every gallery arrives like a private screening — for working photographers.';
const DEFAULT_DESCRIPTION = SITE_DESCRIPTION;
const DEFAULT_KEYWORDS =
  'photography app, pose ideas, photography poses, portrait poses, wedding photography, fashion photography, photo shoot poses, booking management, shot lists, photography community, photographer tools, posing guide, photography inspiration, photography challenges, trending poses';
const SITE_URL = 'https://musephoto.app';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png?v=3`;
const DEFAULT_OG_IMAGE_ALT =
  "Muse — dark editorial card: a small app-icon badge above the Playfair wordmark and the line 'Find the pose. Plan the shoot. Deliver the gallery.' beside a warm portrait of a couple.";

const SEO: React.FC<SEOProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = DEFAULT_OG_IMAGE_ALT,
  ogType = 'website',
  // Default to noindex off prod. UAT / preview hosts must never be indexed;
  // callers that already pass noIndex (BookingPage, NotFound, gallery notices)
  // keep forcing it on prod too. Canonicals still point at prod — the correct
  // SEO signal for a noindex mirror of the production site.
  noIndex = !IS_PROD,
  structuredData,
  article,
  appBanner = true,
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = canonicalUrl || SITE_URL;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}
      <link rel="canonical" href={canonical} />

      {/* iOS Smart App Banner — live only. Apple shows a "View" button for
          un-installed pre-order apps (wrong UX), so the banner stays off
          until the app is downloadable. app-argument carries the canonical
          so a tap deep-links to the same page via the universal link. */}
      {appBanner && LAUNCH_PHASE === 'live' && APP_STORE_ID && (
        <meta
          name="apple-itunes-app"
          content={`app-id=${APP_STORE_ID}, app-argument=${canonical}`}
        />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      {article?.publishedTime && (
        <meta
          property="article:published_time"
          content={article.publishedTime}
        />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData).replace(/</g, '\\u003c')}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
